import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  FiAlertCircle,
  FiBriefcase,
  FiHome,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEdit2,
  FiFileText,
  FiMail,
  FiMapPin,
  FiPhone,
  FiPrinter,
  FiShield,
  FiSend,
  FiUser,
  FiUserCheck,
  FiUserX,
  FiXCircle,
} from 'react-icons/fi';
import { useReactToPrint } from 'react-to-print';

import BasePage from '../../../components/BasePage';
import LoadingState from '../../../components/LoadingState';
import api from '../../../services/api';
import { formatCpf } from '../../../utils/cpfMask';

import EditWorkerModal from './EditWorkerModal';
import ForwardWorkerModal from './ForwardWorkerModal';
import RemoveFromSyndicateModal from './RemoveFromSyndicateModal';

import {
  Avatar,
  Banner,
  Card,
  ConsentItem,
  ConsentList,
  DangerButton,
  Field,
  GhostButton,
  HeaderActions,
  IconOnly,
  Identity,
  InfoGrid,
  Page,
  PrimaryButton,
  ProfileHeader,
  SectionBody,
  SectionHeader,
  StatusChip,
} from './styles';

interface ICity {
  nome: string;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}

interface ICompany {
  id: string;
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  address: string;
  state: string;
  postal_code: string;
}

interface IWorker {
  id: string;
  cpf: string;
  first_name: string;
  last_name: string;
  rg?: string;
  activity_profile?: 'rural' | 'industrial' | 'market' | 'public-service';
  is_peasant?: boolean;
  is_outsourced?: boolean;
  worker_function?: string;
  genre: 'male' | 'female' | 'other';
  phone: string;
  situation?: 'temporary' | 'permanent';
  email?: string;
  address?: string;
  city_id?: number;
  state?: string;
  postal_code?: string;
  is_temporary_address?: boolean;
  origin_address?: string;
  origin_city_id?: number;
  origin_state?: string;
  origin_postal_code?: string;
  salary_range?: number;
  is_unionized?: boolean;
  is_syndicate_approved: boolean | null;
  syndicate_approved_at: Date;
  syndicate_id?: string;
  company?: ICompany;
  city: ICity;
  syndicate: ISyndicate;
  is_discount_agreed: boolean;
  is_lgpd_agreed: boolean;
  is_privacy_terms_agreed: boolean;
  created_at: Date;
}

interface IBGEState {
  id: number;
  nome: string;
  uf: string;
}

interface IBGECity {
  id: number;
  nome: string;
}

const ACTIVITY_LABELS: Record<string, string> = {
  rural: 'Rural',
  industrial: 'Industrial',
  market: 'Comércio',
  'public-service': 'Serviço Público',
};

const GENRE_LABELS: Record<string, string> = {
  male: 'Masculino',
  female: 'Feminino',
  other: 'Outro',
};

const SALARY_LABELS: Record<number, string> = {
  1: 'Menos que 1 salário mínimo',
  2: 'Até 1 salário mínimo',
  3: 'De 1 a 2 salários mínimos',
  4: 'De 2 a 4 salários mínimos',
  5: 'Maior que 4 salários mínimos',
};

const getInitials = (first?: string, last?: string) =>
  `${(first || '?')[0]}${(last || '')[0]}`.toUpperCase();

const PRINT_STYLE = `
  @page {
    margin: 64px;
    margin-top: 128px;
  }
  @media print {
    .pagebreak { page-break-before: always; }
    body { background: white !important; }
  }
`;

const Value: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  if (children === undefined || children === null || children === '') {
    return <span className="value empty">Não informado</span>;
  }
  return <span className="value">{children}</span>;
};

Value.defaultProps = {
  children: undefined,
};

const Profile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const printRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [worker, setWorker] = useState<IWorker>({} as IWorker);
  const [states, setStates] = useState<IBGEState[]>([]);
  const [cities, setCities] = useState<IBGECity[]>([]);

  const [showEdit, setShowEdit] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [showForward, setShowForward] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Ficha - ${worker.first_name} ${worker.last_name}`,
    pageStyle: () => PRINT_STYLE,
  });

  const handleWorkerApproval = useCallback(
    async (action: 'approved' | 'rejected') => {
      const payload =
        action === 'approved'
          ? { is_syndicate_approved: true, is_unionized: true }
          : { is_syndicate_approved: null, is_unionized: false };
      await api.put(`/workers/${id}`, payload);
      const fresh = await api.get(`/workers/${id}`);
      setWorker(fresh.data);
    },
    [id],
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const loadedWorker = await api.get(`/workers/${id}`);
      const loadedStates = await api.get(`/ibge/states`);
      const loadedCities = loadedWorker.data.state
        ? await api.get(`/ibge/cities/${loadedWorker.data.state}`)
        : { data: [] };

      setWorker(loadedWorker.data);
      setStates(loadedStates.data);
      setCities(loadedCities.data);
      setLoading(false);
    };
    load();
  }, [id]);

  const stateName = useMemo(
    () => states.find(s => s.uf === worker.state)?.nome,
    [states, worker.state],
  );
  const cityName = useMemo(
    () => cities.find(c => c.id === worker.city_id)?.nome,
    [cities, worker.city_id],
  );
  const originStateName = useMemo(
    () => states.find(s => s.uf === worker.origin_state)?.nome,
    [states, worker.origin_state],
  );
  const originCityName = useMemo(
    () => cities.find(c => c.id === worker.origin_city_id)?.nome,
    [cities, worker.origin_city_id],
  );

  return (
    <BasePage title="Trabalhador">
      {loading && <LoadingState />}
      {!loading && worker && (
        <div ref={printRef}>
          <Page>
            {/* Header card */}
            <Card>
              <ProfileHeader>
                <Avatar>
                  {getInitials(worker.first_name, worker.last_name)}
                </Avatar>
                <Identity>
                  <h1>
                    {worker.first_name}
                    {' '}
                    {worker.last_name}
                  </h1>
                  <div className="meta">
                    <span>
                      <FiFileText size={13} />
                      {formatCpf(worker.cpf)}
                    </span>
                    {worker.phone && (
                      <span>
                        <FiPhone size={13} />
                        {worker.phone}
                      </span>
                    )}
                    {worker.activity_profile && (
                      <span>
                        <FiBriefcase size={13} />
                        {ACTIVITY_LABELS[worker.activity_profile]}
                      </span>
                    )}
                    {worker.is_syndicate_approved === true && (
                      <StatusChip $tone="success">
                        <span className="dot" />
                        Sindicalizado
                      </StatusChip>
                    )}
                    {worker.is_syndicate_approved === false && (
                      <StatusChip $tone="warning">
                        <span className="dot" />
                        Aguardando aprovação
                      </StatusChip>
                    )}
                    {worker.is_syndicate_approved === null && (
                      <StatusChip $tone="neutral">
                        <span className="dot" />
                        Não associado
                      </StatusChip>
                    )}
                  </div>
                </Identity>
                <HeaderActions className="header-actions">
                  <IconOnly
                    type="button"
                    title="Imprimir"
                    onClick={handlePrint}
                  >
                    <FiPrinter size={16} />
                  </IconOnly>
                  <GhostButton type="button" onClick={() => setShowEdit(true)}>
                    <FiEdit2 size={14} />
                    Editar
                  </GhostButton>
                  <DangerButton
                    type="button"
                    onClick={() => setShowRemove(true)}
                  >
                    <FiUserX size={14} />
                    Remover do sindicato
                  </DangerButton>
                </HeaderActions>
              </ProfileHeader>
            </Card>

            {/* Status banner */}
            {worker.is_syndicate_approved === null && (
              <Banner $tone="info">
                <div className="icon">
                  <FiAlertCircle size={20} />
                </div>
                <div className="content">
                  <div className="title">Não associado a um sindicato</div>
                  <div className="desc">
                    Este trabalhador ainda não está vinculado a nenhum
                    sindicato.
                  </div>
                </div>
              </Banner>
            )}

            {worker.is_syndicate_approved === true && (
              <Banner $tone="success">
                <div className="icon">
                  <FiUserCheck size={20} />
                </div>
                <div className="content">
                  <div className="title">
                    Associado a
                    {' '}
                    {worker.syndicate?.nome_fantasia || 'um sindicato'}
                  </div>
                  <div className="desc">
                    {worker.syndicate_approved_at && (
                      <>
                        Sindicalizado desde
                        {' '}
                        {format(
                          new Date(worker.syndicate_approved_at),
                          "dd/MM/yyyy 'às' HH:mm",
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Banner>
            )}

            {worker.is_syndicate_approved === false && (
              <Banner $tone="warning">
                <div className="icon">
                  <FiClock size={20} />
                </div>
                <div className="content">
                  <div className="title">Aguardando aprovação</div>
                  <div className="desc">
                    Sindicato solicitado:
                    {' '}
                    <strong>{worker.syndicate?.nome_fantasia || '—'}</strong>
                  </div>
                </div>
                <div className="actions">
                  <PrimaryButton
                    type="button"
                    onClick={() => handleWorkerApproval('approved')}
                  >
                    <FiCheckCircle size={14} />
                    Aprovar
                  </PrimaryButton>
                  <DangerButton
                    type="button"
                    onClick={() => handleWorkerApproval('rejected')}
                  >
                    <FiXCircle size={14} />
                    Rejeitar
                  </DangerButton>
                  <GhostButton
                    type="button"
                    onClick={() => setShowForward(true)}
                  >
                    <FiSend size={14} />
                    Encaminhar
                  </GhostButton>
                </div>
              </Banner>
            )}

            {/* Personal info */}
            <Card>
              <SectionHeader>
                <FiUser size={14} />
                <h3>Informações pessoais</h3>
              </SectionHeader>
              <SectionBody>
                <InfoGrid>
                  <Field>
                    <span className="label">CPF</span>
                    <Value>{formatCpf(worker.cpf)}</Value>
                  </Field>
                  <Field>
                    <span className="label">RG</span>
                    <Value>{worker.rg}</Value>
                  </Field>
                  <Field>
                    <span className="label">Gênero</span>
                    <Value>
                      {worker.genre ? GENRE_LABELS[worker.genre] : null}
                    </Value>
                  </Field>
                  <Field>
                    <span className="label">
                      <FiPhone
                        size={11}
                        style={{ marginRight: 4, verticalAlign: -1 }}
                      />
                      Telefone
                    </span>
                    <Value>{worker.phone}</Value>
                  </Field>
                  <Field $span={2}>
                    <span className="label">
                      <FiMail
                        size={11}
                        style={{ marginRight: 4, verticalAlign: -1 }}
                      />
                      E-mail
                    </span>
                    <Value>{worker.email}</Value>
                  </Field>
                </InfoGrid>
              </SectionBody>
            </Card>

            {/* Activity */}
            <Card>
              <SectionHeader>
                <FiBriefcase size={14} />
                <h3>Perfil de atividade</h3>
              </SectionHeader>
              <SectionBody>
                <InfoGrid>
                  <Field>
                    <span className="label">Perfil de atividade</span>
                    <Value>
                      {worker.activity_profile
                        ? ACTIVITY_LABELS[worker.activity_profile]
                        : null}
                    </Value>
                  </Field>
                  <Field>
                    <span className="label">Situação</span>
                    <Value>
                      {(() => {
                        if (worker.situation === 'permanent') return 'Permanente';
                        if (worker.situation === 'temporary') return 'Temporário';
                        return null;
                      })()}
                    </Value>
                  </Field>
                  <Field>
                    <span className="label">Função</span>
                    <Value>{worker.worker_function}</Value>
                  </Field>
                  {worker.activity_profile === 'rural' && (
                    <Field>
                      <span className="label">Safrista</span>
                      <Value>
                        {(() => {
                          if (worker.is_peasant === true) return 'Sim';
                          if (worker.is_peasant === false) return 'Não';
                          return null;
                        })()}
                      </Value>
                    </Field>
                  )}
                  {worker.activity_profile === 'industrial' && (
                    <Field>
                      <span className="label">Terceirizado</span>
                      <Value>
                        {(() => {
                          if (worker.is_outsourced === true) return 'Sim';
                          if (worker.is_outsourced === false) return 'Não';
                          return null;
                        })()}
                      </Value>
                    </Field>
                  )}
                  <Field $span={2}>
                    <span className="label">Faixa salarial</span>
                    <Value>
                      {worker.salary_range
                        ? SALARY_LABELS[worker.salary_range]
                        : null}
                    </Value>
                  </Field>
                </InfoGrid>
              </SectionBody>
            </Card>

            {/* Empresa */}
            {worker.company && (
              <Card>
                <SectionHeader>
                  <FiHome size={14} />
                  <h3>Empresa</h3>
                </SectionHeader>
                <SectionBody>
                  <InfoGrid>
                    <Field>
                      <span className="label">Nome fantasia</span>
                      <Value>{worker.company.nome_fantasia}</Value>
                    </Field>
                    <Field>
                      <span className="label">Razão social</span>
                      <Value>{worker.company.razao_social}</Value>
                    </Field>
                    <Field>
                      <span className="label">CNPJ</span>
                      <Value>{worker.company.cnpj}</Value>
                    </Field>
                    <Field $span={2}>
                      <span className="label">Endereço</span>
                      <Value>{worker.company.address}</Value>
                    </Field>
                    <Field>
                      <span className="label">Estado</span>
                      <Value>{worker.company.state}</Value>
                    </Field>
                    <Field>
                      <span className="label">CEP</span>
                      <Value>{worker.company.postal_code}</Value>
                    </Field>
                  </InfoGrid>
                </SectionBody>
              </Card>
            )}

            {/* Address */}
            <Card>
              <SectionHeader>
                <FiMapPin size={14} />
                <h3>Endereço</h3>
              </SectionHeader>
              <SectionBody>
                <InfoGrid>
                  <Field>
                    <span className="label">CEP</span>
                    <Value>{worker.postal_code}</Value>
                  </Field>
                  <Field $span={2}>
                    <span className="label">Endereço</span>
                    <Value>{worker.address}</Value>
                  </Field>
                  <Field>
                    <span className="label">Estado</span>
                    <Value>{stateName || worker.state}</Value>
                  </Field>
                  <Field>
                    <span className="label">Cidade</span>
                    <Value>{cityName || worker.city?.nome}</Value>
                  </Field>
                  <Field>
                    <span className="label">Endereço temporário</span>
                    <Value>
                      {(() => {
                        if (worker.is_temporary_address === true) return 'Sim';
                        if (worker.is_temporary_address === false) return 'Não';
                        return null;
                      })()}
                    </Value>
                  </Field>
                </InfoGrid>

                {worker.is_temporary_address && (
                  <>
                    <hr
                      style={{
                        margin: '20px 0',
                        border: 'none',
                        borderTop: '1px dashed #e2e8f0',
                      }}
                    />
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        marginBottom: 12,
                      }}
                    >
                      Endereço de origem
                    </div>
                    <InfoGrid>
                      <Field>
                        <span className="label">CEP de origem</span>
                        <Value>{worker.origin_postal_code}</Value>
                      </Field>
                      <Field $span={2}>
                        <span className="label">Endereço de origem</span>
                        <Value>{worker.origin_address}</Value>
                      </Field>
                      <Field>
                        <span className="label">Estado de origem</span>
                        <Value>{originStateName || worker.origin_state}</Value>
                      </Field>
                      <Field>
                        <span className="label">Cidade de origem</span>
                        <Value>{originCityName}</Value>
                      </Field>
                    </InfoGrid>
                  </>
                )}
              </SectionBody>
            </Card>

            {/* Consentimentos e datas */}
            <Card>
              <SectionHeader>
                <FiShield size={14} />
                <h3>Cadastro e consentimentos</h3>
              </SectionHeader>
              <SectionBody>
                <InfoGrid>
                  <Field>
                    <span className="label">
                      <FiCalendar
                        size={11}
                        style={{ marginRight: 4, verticalAlign: -1 }}
                      />
                      Cadastrado desde
                    </span>
                    <Value>
                      {worker.created_at
                        ? format(
                            new Date(worker.created_at),
                            "dd/MM/yyyy 'às' HH:mm",
                          )
                        : null}
                    </Value>
                  </Field>
                  {worker.is_syndicate_approved === true && (
                    <Field>
                      <span className="label">
                        <FiUserCheck
                          size={11}
                          style={{ marginRight: 4, verticalAlign: -1 }}
                        />
                        Sindicalizado desde
                      </span>
                      <Value>
                        {worker.syndicate_approved_at
                          ? format(
                              new Date(worker.syndicate_approved_at),
                              "dd/MM/yyyy 'às' HH:mm",
                            )
                          : null}
                      </Value>
                    </Field>
                  )}
                </InfoGrid>

                <div style={{ height: 18 }} />

                <ConsentList>
                  <ConsentItem $agreed={!!worker.is_lgpd_agreed}>
                    <div className="icon">
                      {worker.is_lgpd_agreed ? (
                        <FiCheckCircle size={16} />
                      ) : (
                        <FiXCircle size={16} />
                      )}
                    </div>
                    <div className="text">
                      <span className="title">
                        Compartilhamento com sindicato
                      </span>
                      <span className="status">
                        {worker.is_lgpd_agreed
                          ? 'Autorizado'
                          : 'Não autorizado'}
                      </span>
                    </div>
                  </ConsentItem>

                  <ConsentItem $agreed={!!worker.is_discount_agreed}>
                    <div className="icon">
                      {worker.is_discount_agreed ? (
                        <FiCheckCircle size={16} />
                      ) : (
                        <FiXCircle size={16} />
                      )}
                    </div>
                    <div className="text">
                      <span className="title">Desconto de 1% em folha</span>
                      <span className="status">
                        {worker.is_discount_agreed
                          ? 'Autorizado'
                          : 'Não autorizado'}
                      </span>
                    </div>
                  </ConsentItem>
                </ConsentList>
              </SectionBody>
            </Card>
          </Page>

          <RemoveFromSyndicateModal
            workerId={id}
            handleShowModal={setShowRemove}
            showModal={showRemove}
          />

          <EditWorkerModal
            handleWorker={setWorker}
            worker={worker}
            handleShowModal={setShowEdit}
            showModal={showEdit}
          />

          <ForwardWorkerModal
            worker={worker}
            show={showForward}
            handleShow={setShowForward}
          />
        </div>
      )}
    </BasePage>
  );
};

export default Profile;
